import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import * as policiesApi from "@/services/policiesApi";
import type { PolicyDetails } from "@/types/api";
import { useDevices } from "@/hooks/useDevices";
import type { Device } from "@/types/device";
import type { UpdateStatus } from "@/components/software-updates/StatusBadge";

/** True for any payload kind related to Apple software updates. */
export function isSoftwareUpdatePayload(kind?: string): boolean {
  if (!kind) return false;
  const k = kind.toLowerCase();
  return (
    k.includes("softwareupdate") ||
    k.includes("software-update") ||
    k.includes("os.update") ||
    k.includes("osupdate")
  );
}

export function isSoftwareUpdatePolicy(p: PolicyDetails): boolean {
  if (p.payloadType && isSoftwareUpdatePayload(p.payloadType)) return true;
  return (p.payloads ?? []).some((pl) => isSoftwareUpdatePayload(pl.kind));
}

export function useSoftwareUpdatePolicies() {
  const query = useQuery({
    queryKey: ["policies", "software-updates"],
    queryFn: () => policiesApi.listPolicies(),
    staleTime: 30_000,
  });

  const policies = useMemo(
    () => (query.data ?? []).filter(isSoftwareUpdatePolicy),
    [query.data]
  );

  return { ...query, policies };
}

function parseSemver(v?: string | null): number[] {
  if (!v) return [];
  return String(v)
    .split(/[^\d]+/)
    .filter(Boolean)
    .map((n) => parseInt(n, 10));
}

function compareSemver(a: number[], b: number[]): number {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (av !== bv) return av - bv;
  }
  return 0;
}

export interface DeviceUpdateRow {
  device: Device;
  family: string;
  current: string;
  latest: string;
  status: UpdateStatus;
}

/** Buckets devices by OS family (ios, ipados, macos, tvos, …). */
function osFamily(d: Device): string {
  const t = (d.os_type || d.platform || "").toLowerCase();
  if (t.includes("ipad")) return "ipados";
  if (t.includes("ios") || t.includes("iphone")) return "ios";
  if (t.includes("mac")) return "macos";
  if (t.includes("tv")) return "tvos";
  if (t.includes("watch")) return "watchos";
  return t || "unknown";
}

export function useDeviceUpdateRows() {
  const devicesQuery = useDevices({ pageSize: 200 });
  const devices: Device[] = devicesQuery.data ?? [];

  const rows = useMemo<DeviceUpdateRow[]>(() => {
    const latestByFamily = new Map<string, number[]>();
    for (const d of devices) {
      const f = osFamily(d);
      const sv = parseSemver(d.os_version);
      const prev = latestByFamily.get(f);
      if (!prev || compareSemver(sv, prev) > 0) {
        latestByFamily.set(f, sv);
      }
    }
    return devices.map((d) => {
      const f = osFamily(d);
      const current = parseSemver(d.os_version);
      const latestArr = latestByFamily.get(f) ?? [];
      const cmp = compareSemver(current, latestArr);
      let status: UpdateStatus = "unknown";
      if (!d.os_version) status = "unknown";
      else if (cmp >= 0) status = "up_to_date";
      else status = "behind";
      return {
        device: d,
        family: f,
        current: d.os_version || "—",
        latest: latestArr.length ? latestArr.join(".") : "—",
        status,
      };
    });
  }, [devices]);

  const stats = useMemo(() => {
    const total = rows.length;
    const upToDate = rows.filter((r) => r.status === "up_to_date").length;
    const behind = rows.filter((r) => r.status === "behind").length;
    return { total, upToDate, behind, compliance: total ? upToDate / total : 0 };
  }, [rows]);

  const familyDistribution = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) map.set(r.family, (map.get(r.family) ?? 0) + 1);
    return Array.from(map.entries())
      .map(([family, count]) => ({ family, count }))
      .sort((a, b) => b.count - a.count);
  }, [rows]);

  return { rows, stats, familyDistribution, isLoading: devicesQuery.isLoading, error: devicesQuery.error };
}
